import {qualify} from '@packages/systems/wfdl-Qualified';
import {Plugins, Component} from '@packages/systems/wfdl';

import {
  CART_BINDING_CONTEXT_ID,
  CART_ITEMS_BINDING_CONTEXT_ID,
  CART_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT_ID,
  CART_BINDING_CONTEXT_TYPE_ID,
  ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
  PRODUCT_OPTION_BINDING_CONTEXT_TYPE_ID,
} from './identifiers';

import {
  ORDER_QUERY_SPECIFICATION,
  ORDER_ITEMS_QUERY_SPECIFICATION,
} from './order-query-specification';

import type {
  BindingContextDef,
  BindingContextTypeDef,
  BindablePropertiesDef,
  BindingContextRelationshipDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';

export const cartType: BindingContextTypeDef = {
  entity: 'Entity',
  id: CART_BINDING_CONTEXT_TYPE_ID,
  label: 'Cart',
  pluralLabel: 'Carts',
  singularLabel: 'Cart',
  iconName: 'cart',
};

export function cartBindingContexts<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  const cart = {
    variant: 'Entity',
    id: CART_BINDING_CONTEXT_ID,
    label: 'Cart',
    type: typeReferenceBuilder.entityOf({
      entity: 'Entity',
      id: CART_BINDING_CONTEXT_TYPE_ID,
    }),
    matchElement: {
      elementType: qualify(
        Plugins.name('Commerce'),
        Component.name('CommerceCartWrapper')
      ),
    },
    query: ORDER_QUERY_SPECIFICATION,
  } as const;

  const cartItems = {
    variant: 'List',
    id: CART_ITEMS_BINDING_CONTEXT_ID,
    label: 'Products',
    type: typeReferenceBuilder.listOf({
      entity: 'Entity',
      id: ORDER_ITEM_BINDING_CONTEXT_TYPE_ID,
    }),
    matchElement: {
      elementType: qualify(
        Plugins.name('Commerce'),
        Component.name('CommerceCartItem')
      ),
    },
    query: ORDER_ITEMS_QUERY_SPECIFICATION,
  } as const;

  const cartItemProductOptions = {
    variant: 'List',
    id: CART_ITEM_PRODUCT_OPTIONS_BINDING_CONTEXT_ID,
    label: 'Product Options',
    type: typeReferenceBuilder.listOf({
      entity: 'Entity',
      id: PRODUCT_OPTION_BINDING_CONTEXT_TYPE_ID,
    }),
    matchElement: {
      elementType: qualify(
        Plugins.name('Commerce'),
        Component.name('CommerceCartOptionListItem')
      ),
    },
  } as const;

  return [cart, cartItems, cartItemProductOptions];
}

export function cartBindableProperties<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindablePropertiesDef<EntityTRef, ListTRef> {
  return {
    type: typeReferenceBuilder.entityOf({
      entity: 'Entity',
      id: CART_BINDING_CONTEXT_TYPE_ID,
    }),
    bindableProperties: [
      {
        id: 'cart-user-items-quantity',
        type: 'Number',
        label: 'Quantity',
        path: [{id: '', slug: 'userItemsCount', type: 'Number'}],
        queryPath: [],
        meta: {
          format: 'integer',
          precision: 0,
          allowNegative: false,
          minValue: null,
          maxValue: null,
          required: true,
        },
        rendering: {strategy: 'client'},
      },
      {
        id: 'cart-subtotal',
        type: 'CommercePrice',
        label: 'Subtotal',
        path: [{id: '', slug: 'subtotal', type: 'CommercePrice'}],
        queryPath: [],
        meta: {required: true},
        rendering: {strategy: 'client'},
      },
    ],
  };
}

export function cartRelationships<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextRelationshipDef<RootTRef, EntityTRef> {
  return {
    type: typeReferenceBuilder.entityOf({
      entity: 'Entity',
      id: CART_BINDING_CONTEXT_TYPE_ID,
    }),
    relativeContexts: [CART_ITEMS_BINDING_CONTEXT_ID],
  };
}
